import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Autocomplete,
  TextField,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import SaveIcon from '@mui/icons-material/Save';
import { mainApi } from '../../../config/apiClient';
import { useTranslation } from 'react-i18next';

interface QrMenuOption {
  firebaseId: string;
  name: string;
  branchId?: string | null;
  branchName?: string | null;
}

interface KioskKitchenScreenTabProps {
  // KioskSettingsModal tarafında bu iki alan `string | null` olarak modellenmiş;
  // modal açılırken bunlar dolu geliyor ama tip uyumu için nullable kabul ediyoruz.
  kioskId: string | null;
  companyId: string | null;
  kioskBranchId: string | null;
  currentQrMenuFirebaseId: string | null;
  onUpdated: (newFirebaseId: string | null) => void;
}

/**
 * Mutfak Ekrani (QR Menu) sekmesi.
 *
 * Kioska hangi QR menuye (mutfak ekrani) siparis yazilacagini explicit secer.
 * - NULL kayit = "otomatik" (subedeki ilk QR menu fallback) — eski davranis korunur.
 * - Sade UI: subenin QR menulerini cek, dropdown'da goster, Kaydet'e basinca
 *   POST /api/Kiosk/{id}/qrmenu cagrir.
 *
 * State izole, modal global "Kaydet"ine bagli degil (KioskAssignmentTab pattern'i).
 */
const KioskKitchenScreenTab: React.FC<KioskKitchenScreenTabProps> = ({
  kioskId,
  companyId,
  kioskBranchId,
  currentQrMenuFirebaseId,
  onUpdated,
}) => {
  const { t } = useTranslation('salesSystem');

  const [allMenus, setAllMenus] = useState<QrMenuOption[]>([]);
  const [loadingMenus, setLoadingMenus] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // "null" = otomatik (subedeki ilk menu fallback). Aksi halde secilen menu firebaseId'si.
  const [selectedFirebaseId, setSelectedFirebaseId] = useState<string | null>(currentQrMenuFirebaseId);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    setSelectedFirebaseId(currentQrMenuFirebaseId);
  }, [currentQrMenuFirebaseId]);

  const loadMenus = useCallback(async () => {
    if (!companyId) return;
    try {
      setLoadingMenus(true);
      setLoadError(null);

      // ⚠️ ÖNEMLİ: /api/QRMenu/menu-definitions endpoint'i kullanılıyor (QRMenuDefinitions tablosu).
      // /api/QRMenu/menus ise QRMenus tablosundan döner — farklı bir tablo!
      // CreateKioskOrder ve Kiosks.QrMenuFirebaseId hep QRMenuDefinitions.FirebaseId ile çalışır.
      // QuickSale ekranındaki QRMenuSelectionModal de aynı endpoint'i kullanıyor.
      const response = await mainApi.get('/api/QRMenu/menu-definitions');
      const data = response?.data;
      const list: any[] = Array.isArray(data) ? data : (data?.data || []);

      // Cevap formatında `id` aslında FirebaseId'dir (QRMenuController.GetAllMenuDefinitions satır 1335).
      const normalized: QrMenuOption[] = (list || [])
        .map((m: any) => ({
          firebaseId: String(m.id || m.firebaseId || m.FirebaseId || ''),
          name: String(m.name || m.Name || 'İsimsiz Menü'),
          branchId: m.branchId ?? m.BranchId ?? null,
          branchName: m.branchName ?? m.BranchName ?? null,
        }))
        .filter((m: QrMenuOption) => m.firebaseId.length > 0);

      setAllMenus(normalized);
    } catch (err: any) {
      console.error('[KIOSK KITCHEN SCREEN TAB] QR menü listesi alınamadı:', err);
      setLoadError(err?.response?.data?.message || err?.message || 'QR menü listesi alınamadı');
    } finally {
      setLoadingMenus(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadMenus();
  }, [loadMenus]);

  // Yalniz bu subenin menulerini goster (server cevabinda branchId zaten geliyor)
  const branchMenus = useMemo<QrMenuOption[]>(() => {
    if (!kioskBranchId) return allMenus;
    return allMenus.filter((m) => (m.branchId || '') === kioskBranchId);
  }, [allMenus, kioskBranchId]);

  const selectedOption = useMemo<QrMenuOption | null>(() => {
    if (!selectedFirebaseId) return null;
    return branchMenus.find((m) => m.firebaseId === selectedFirebaseId) || null;
  }, [branchMenus, selectedFirebaseId]);

  const hasChanges = (currentQrMenuFirebaseId || null) !== (selectedFirebaseId || null);

  const handleSave = useCallback(async () => {
    if (!kioskId) {
      setSaveError('Kiosk ID alınamadı. Modal yeniden açılıp tekrar deneyin.');
      return;
    }
    try {
      setSaving(true);
      setSaveError(null);
      setSaveSuccess(false);

      const body = { qrMenuFirebaseId: selectedFirebaseId || null };
      const res = await mainApi.post(`/api/Kiosk/${kioskId}/qrmenu`, body);

      if (res?.data?.success) {
        const newVal = res.data?.data?.qrMenuFirebaseId ?? selectedFirebaseId ?? null;
        onUpdated(newVal);
        setSaveSuccess(true);
      } else {
        setSaveError(res?.data?.message || 'Kayıt başarısız');
      }
    } catch (err: any) {
      console.error('[KIOSK KITCHEN SCREEN TAB] QR menü kaydedilemedi:', err);
      setSaveError(err?.response?.data?.message || err?.message || 'QR menü kaydedilemedi');
    } finally {
      setSaving(false);
    }
  }, [kioskId, selectedFirebaseId, onUpdated]);

  return (
    <Box sx={{ p: { xs: 1, md: 2 }, maxWidth: 720 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <RestaurantMenuIcon color="primary" />
        <Typography variant="h6">
          {t('kioskSettings.kitchenScreen.title', 'Mutfak Ekranı (QR Menü)')}
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        {t(
          'kioskSettings.kitchenScreen.description',
          'Bu kioskdan verilen siparişlerin hangi QR menüye (mutfak ekranına) düşeceğini seçin. Boş bırakırsanız sistem şubedeki ilk QR menüyü otomatik kullanır.'
        )}
      </Alert>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLoadError(null)}>
          {loadError}
        </Alert>
      )}

      {loadingMenus ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            {t('kioskSettings.kitchenScreen.loading', 'QR menüler yükleniyor...')}
          </Typography>
        </Box>
      ) : (
        <>
          <Autocomplete<QrMenuOption, false, false, false>
            options={branchMenus}
            value={selectedOption}
            onChange={(_, val) => setSelectedFirebaseId(val ? val.firebaseId : null)}
            getOptionLabel={(opt) => opt?.name || ''}
            isOptionEqualToValue={(a, b) => a.firebaseId === b.firebaseId}
            noOptionsText={
              kioskBranchId
                ? t(
                    'kioskSettings.kitchenScreen.noBranchMenus',
                    'Bu şube için tanımlı QR menü bulunamadı.'
                  )
                : t('kioskSettings.kitchenScreen.noMenus', 'QR menü bulunamadı.')
            }
            renderOption={(props, opt) => (
              <li {...props} key={opt.firebaseId}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body2" fontWeight={500}>{opt.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {opt.branchName || opt.branchId || '—'}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('kioskSettings.kitchenScreen.label', 'QR Menü Seçimi')}
                placeholder={t(
                  'kioskSettings.kitchenScreen.placeholder',
                  'Otomatik (şubedeki ilk menü)'
                )}
                size="small"
              />
            )}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('kioskSettings.kitchenScreen.currentLabel', 'Mevcut Atama:')}
            </Typography>
            {currentQrMenuFirebaseId ? (
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={
                  allMenus.find((m) => m.firebaseId === currentQrMenuFirebaseId)?.name ||
                  currentQrMenuFirebaseId
                }
              />
            ) : (
              <Chip
                size="small"
                variant="outlined"
                label={t('kioskSettings.kitchenScreen.autoMode', 'Otomatik (şubedeki ilk menü)')}
              />
            )}
          </Box>

          {saveError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
              {saveError}
            </Alert>
          )}
          {saveSuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaveSuccess(false)}>
              {t('kioskSettings.kitchenScreen.saveSuccess', 'QR menü ataması güncellendi.')}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saving || !hasChanges}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              size="small"
            >
              {saving
                ? t('kioskSettings.actions.saving', 'Kaydediliyor...')
                : t('kioskSettings.actions.saveChanges', 'Değişiklikleri Kaydet')}
            </Button>

            {selectedFirebaseId !== null && (
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => setSelectedFirebaseId(null)}
                disabled={saving}
                size="small"
              >
                {t('kioskSettings.kitchenScreen.resetAuto', 'Otomatik Moda Dön')}
              </Button>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default KioskKitchenScreenTab;
